import { FC } from 'react';
import styled from 'styled-components';
import { InsightPanelSize } from './types';
import { InsightHeader } from './InsightHeader';
import { CustomInsightHeader } from './CustomInsightHeader';
import { InsightType } from '../domain';

export type InsightPanelProps = {
  title?: string | React.ReactNode;
  colSpan?: number;
  className?: string;
  showDivider?: boolean;
  showSelect?: boolean;
  type?: InsightType;
  size?: InsightPanelSize;
};

export const Wrapper = styled.div<{ $colSpan?: number; $isCustom: boolean }>`
  padding: ${({ theme }) => `
    ${theme.spacing.values.base} ${theme.spacing.values.base} ${theme.spacing.values.base} ${theme.spacing.values.plusHalf}
  `};
  margin-bottom: ${({ theme }) => theme.spacing.values.base};
  border: ${({ theme }) => theme.spacing.values.single} solid #d6d6d6;
  grid-column-end: span ${({ $colSpan = 1 }) => $colSpan};
  border-radius: ${({ theme }) => theme.spacing.values.half};
  display: flex;
  flex-direction: column;
  ${({ theme }) => theme.elevations.smallShadow};

  ${({ $isCustom }) =>
    $isCustom &&
    `
    animation: fadeIn 1.5s;
    animation-timing-function: cubic-bezier(0, 0, 0.34, 0.95);
    transition-duration: 1.5s;

    @keyframes fadeIn {
      0%, 25% {
        opacity: 0;
      }

      100% { 
        opacity: 1; 
      }
    };
  `};
`;

export const INSIGHT_PANEL_DATA_TEST = 'insights-panel';

export const InsightPanel: FC<React.PropsWithChildren<InsightPanelProps>> = ({
  title,
  colSpan,
  children,
  className,
  type,
  showDivider,
  showSelect,
  size = InsightPanelSize.medium,
}) => {
  /**
   * Separates custom header from normal header as custom has added functionality (like toggle and delete)
   * and would avoid unneeded api calls if using normal
   * */

  const Header = type ? CustomInsightHeader : InsightHeader;

  return (
    <Wrapper
      $colSpan={colSpan}
      className={className}
      data-test={INSIGHT_PANEL_DATA_TEST}
      $isCustom={!!type}
    >
      <Header
        type={type}
        title={title}
        showDivider={showDivider}
        showSelect={showSelect}
        size={size}
      />
      {children}
    </Wrapper>
  );
};
